import { useNavigate } from 'react-router-dom'
import { Activity, CheckCircle, Briefcase, Calendar, Plus, RefreshCw } from 'lucide-react'

export default function ProductivityWidget({ todayAttendance, todayTimesheet, upcomingDeadlines, leaveStatus, loadingProductivity, fetchProductivityData }) {
  const navigate = useNavigate()

  return (
    <div className="relative backdrop-blur-xl bg-white/70 shadow-xl rounded-3xl border border-white/40 p-6 overflow-hidden">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>

      <div className="relative flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2 bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
          <Activity className="w-5 h-5 text-blue-600" />
          Produktivitas Hari Ini
        </h3>
        <button
          onClick={() => fetchProductivityData()}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh data"
        >
          <RefreshCw className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {loadingProductivity ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Memuat data...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Attendance Status */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="bg-blue-500 p-1.5 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">Absensi</span>
              </div>
              {todayAttendance ? (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                  ✓ {todayAttendance.check_out ? 'Selesai' : 'Check In'}
                </span>
              ) : (
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
                  Belum Check In
                </span>
              )}
            </div>
            {todayAttendance && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Masuk:</span>
                  <span className="ml-1 font-bold text-green-600">{todayAttendance.check_in}</span>
                </div>
                {todayAttendance.check_out && (
                  <div>
                    <span className="text-gray-600">Pulang:</span>
                    <span className="ml-1 font-bold text-red-600">{todayAttendance.check_out}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Timesheet Status */}
          <div className="bg-gradient-to-r from-blue-50 to-pink-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="bg-blue-500 p-1.5 rounded-lg">
                  <Briefcase className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700">Timesheet</span>
              </div>
              <span className="text-lg font-bold text-blue-600">{todayTimesheet.length}</span>
            </div>
            <p className="text-xs text-gray-600">
              {todayTimesheet.length > 0
                ? `${todayTimesheet.length} project dikerjakan hari ini`
                : 'Belum ada timesheet hari ini'
              }
            </p>
          </div>

          {/* Upcoming Deadlines */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-orange-500 p-1.5 rounded-lg">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-700">Deadline Minggu Ini</span>
            </div>
            {upcomingDeadlines.length > 0 ? (
              <div className="space-y-2">
                {upcomingDeadlines.map((project) => (
                  <div key={project.project_id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 truncate flex-1">{project.title}</span>
                    <span className="ml-2 text-orange-600 font-medium whitespace-nowrap">
                      {new Date(project.end_date).toLocaleDateString('id-ID', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-600">Tidak ada deadline minggu ini</p>
            )}
          </div>

          {/* Leave Status */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="bg-green-500 p-1.5 rounded-lg">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-sm font-semibold text-gray-700">Status Cuti</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div>
                <div className="font-bold text-gray-700">{leaveStatus.total}</div>
                <div className="text-gray-600">Total</div>
              </div>
              <div>
                <div className="font-bold text-yellow-600">{leaveStatus.pending}</div>
                <div className="text-gray-600">Pending</div>
              </div>
              <div>
                <div className="font-bold text-green-600">{leaveStatus.approved}</div>
                <div className="text-gray-600">Approved</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Quick Actions
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/attendance')}
                className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-500 to-blue-500 text-white rounded-xl text-sm font-medium hover:from-blue-600 hover:to-blue-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Absensi</span>
              </button>
              <button
                onClick={() => navigate('/timesheet')}
                className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-500 to-pink-500 text-white rounded-xl text-sm font-medium hover:from-blue-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <Briefcase className="w-4 h-4" />
                <span>Timesheet</span>
              </button>
              <button
                onClick={() => navigate('/leave-request')}
                className="flex items-center gap-2 p-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-medium hover:from-green-600 hover:to-emerald-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <Calendar className="w-4 h-4" />
                <span>Cuti</span>
              </button>
              <button
                onClick={() => navigate('/add-project')}
                className="flex items-center gap-2 p-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-orange-600 hover:to-amber-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              >
                <Plus className="w-4 h-4" />
                <span>Project</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
